import type { Award, RoomView, SeatNo } from '@chipless/shared';
import { Badge, Button, Chip, Group, Paper, SimpleGrid, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { ptWithUnit, seatLabel } from '../format.js';
import { useStore } from '../store.js';
import classes from './ShowdownPanel.module.css';

/**
 * ショーダウンの勝者選択（要件9.1〜9.4）。
 *
 * ポットごとに獲得資格者のみを並べ、複数選択でスプリットを表す。すべてのポットの勝者が
 * 揃うまで確定できない。
 *
 * 画面下部のシートとして出す。ポットを横に並べることで、サイドポットが増えても縦に
 * 伸びない。プレイヤー端末のアクションバーと同じ「下から出て操作を求める」形に揃えている。
 */
export function ShowdownPanel({ view }: { view: RoomView }): React.ReactElement | null {
  const hand = view.hand;
  const send = useStore((s) => s.send);
  const pending = useStore((s) => s.pending);
  const [winners, setWinners] = useState<Record<number, SeatNo[]>>({});

  const pots = hand?.pots ?? [];
  // ポット数は最大でも席数程度で、毎レンダリングの走査で足りる。`pots` は毎回新しい配列に
  // なるため useMemo を挟んでも意味が無い。
  const complete = pots.length > 0 && pots.every((_, i) => (winners[i] ?? []).length > 0);

  if (hand === null || !hand.needsAward) return null;

  const toggle = (potIndex: number, seatNo: SeatNo): void => {
    setWinners((prev) => {
      const current = prev[potIndex] ?? [];
      const next = current.includes(seatNo)
        ? current.filter((s) => s !== seatNo)
        : [...current, seatNo];
      return { ...prev, [potIndex]: next };
    });
  };

  const confirm = (): void => {
    const awards: Award[] = pots.map((_, potIndex) => ({
      potIndex,
      winners: winners[potIndex] ?? [],
    }));
    send({ type: 'pot:award', handSeq: hand.handSeq, awards });
    setWinners({});
  };

  const nameOf = (seatNo: SeatNo): string => {
    const seat = view.seats[seatNo];
    return seat === undefined || seat.name === '' ? seatLabel(seatNo) : seat.name;
  };

  return (
    <div className={classes.sheet} data-testid="showdown-panel">
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="lg">
        <Stack gap={2}>
          <Group gap="sm">
            <Badge color="yellow" size="lg" variant="filled">
              ショーダウン
            </Badge>
            <Text size="sm" c="dimmed">
              ポットごとに勝者を選んでください。複数選ぶとスプリットになります。
            </Text>
          </Group>
          {!complete && (
            <Text size="xs" c="yellow.4">
              すべてのポットで勝者を選んでください
            </Text>
          )}
        </Stack>

        {/*
          取り消しはここに置かない。運営操作のパネルにあるものと重複し、同じ操作の入口が
          2つある状態になる。
        */}
        <Button disabled={!complete || pending} onClick={confirm} data-testid="showdown-confirm">
          勝者を確定する
        </Button>
      </Group>

      {/* ポットは横に並べる。中央ディスプレイは横長なので、縦を使わずに済む。 */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm" mt="sm">
        {pots.map((pot, potIndex) => (
          <Paper
            key={potIndex}
            withBorder
            p="sm"
            radius="md"
            data-testid={`showdown-pot-${potIndex}`}
          >
            <Stack gap={6}>
              <Text fw={700}>
                {potIndex === 0 ? 'メインポット' : `サイドポット ${potIndex}`}{' '}
                {ptWithUnit(pot.amount)}
              </Text>
              <Group gap="xs">
                {pot.eligible.map((seatNo) => (
                  <Chip
                    key={seatNo}
                    size="md"
                    checked={(winners[potIndex] ?? []).includes(seatNo)}
                    onChange={() => toggle(potIndex, seatNo)}
                    data-testid={`showdown-winner-${potIndex}-${seatNo}`}
                  >
                    {nameOf(seatNo)}
                  </Chip>
                ))}
              </Group>
            </Stack>
          </Paper>
        ))}
      </SimpleGrid>
    </div>
  );
}
